"use client";
import "@/app/globals.css";
import Link from "next/link";
import { ModeToggle } from "@/components/theme-btn";
import ClientOnly from "@/components/client-only";
import { useMobileMenu } from "@/hooks/useMobileMenu";
import AuthConditional from "@/components/AuthConditional";
import LogoutButton from "@/components/LogoutButton";
import { useAuth } from "@/contexts/authContext";
import Image from "next/image";
export default function LayoutHeader() {
  const { isOpen, toggleMenu, pathname } = useMobileMenu();
  const { isAuthenticated } = useAuth();

  return (
    <header className="border-b border-gray-200 dark:border-gray-800 p-1 sticky top-0 w-full z-[199] backdrop-blur-2xl bg-stone-50/90 dark:bg-neutral-800/90 shadow-sm ">
      <div className="container mx-auto">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <Link
              href="/"
              className="text-xl font-bold hover:opacity-80 transition-opacity"
              prefetch={true}
            >
              <div className=" flex items-center justify-center rounded-full ">
                <Image
                  src="/public/Logo_SocialGraphs.png"
                  alt="SocialGraphs Logo"
                  width={50}
                  height={50}
                  className="rounded-full object-cover"
                />
              </div>
            </Link>
            <nav className="hidden md:block">
              <ul className="flex space-x-4">
                <li>
                  <Link
                    href="/explore"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/explore"
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                  >
                    Explore
                  </Link>
                </li>
                <li>
                  <Link
                    href="/clips"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/clips" || pathname.startsWith("/clips/")
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                  >
                    Clips
                  </Link>
                </li>
                <li>
                  <Link
                    href="/marketplace"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/marketplace" ||
                      pathname.startsWith("/marketplace/")
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                  >
                    Marketplace
                  </Link>
                </li>
                <li>
                  <Link
                    href="/elections"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/elections" ||
                      pathname.startsWith("/elections/")
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                    onClick={toggleMenu}
                  >
                    Elections
                  </Link>
                </li>
                <li>
                  <Link
                    href="/about"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/about"
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                  >
                    About
                  </Link>
                </li>
                <li></li>
                <li>
                  <Link
                    href="/support"
                    className={`px-3 py-1 rounded-md transition-colors ${
                      pathname === "/support"
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "hover:text-primary dark:hover:text-primary-foreground"
                    }`}
                  >
                    Support
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="hidden md:flex items-center space-x-2 md:space-x-4">
              <AuthConditional
                authenticated={
                  <LogoutButton className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded-full border border-gray-300 dark:border-gray-700 hover:bg-destructive hover:text-destructive-foreground transition-colors" />
                }
                unauthenticated={
                  <>
                    <Link
                      href="/login"
                      className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded-full border border-gray-300 dark:border-gray-700 hover:bg-secondary transition-colors"
                    >
                      Log in
                    </Link>
                    <Link
                      href="/signup"
                      className="px-3 sm:px-4 py-2 text-sm sm:text-base rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Sign up
                    </Link>
                  </>
                }
              />
            </div>
            <ClientOnly>
              <ModeToggle />
            </ClientOnly>
            <ClientOnly>
              <button
                onClick={toggleMenu}
                className="md:hidden relative flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-200"
              >
                <svg
                  className={`ham hamRotate ham4 ${isOpen ? "active" : ""}`}
                  viewBox="0 0 100 100"
                  width="50"
                  height="50"
                >
                  <path
                    className="line top"
                    d="m 70,33 h -40 c 0,0 -8.5,-0.149796 -8.5,8.5 0,8.649796 8.5,8.5 8.5,8.5 h 20 v -20"
                    stroke="currentColor"
                    fill="none"
                  />
                  <path
                    className="line middle"
                    d="m 70,50 h -40"
                    stroke="currentColor"
                    fill="none"
                  />
                  <path
                    className="line bottom"
                    d="m 30,67 h 40 c 0,0 8.5,0.149796 8.5,-8.5 0,-8.649796 -8.5,-8.5 -8.5,-8.5 h -20 v 20"
                    stroke="currentColor"
                    fill="none"
                  />
                </svg>
              </button>
            </ClientOnly>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        <div
          className={`${
            isOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          } absolute left-0 right-0 bg-stone-100/90 dark:bg-neutral-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-md z-[200] transition-opacity duration-300 md:hidden`}
        >
          <nav className="flex flex-col space-y-4 p-4">
            <Link
              href="/explore"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/explore"
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              Explore
            </Link>
            <Link
              href="/clips"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/clips" || pathname.startsWith("/clips/")
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              Clips
            </Link>
            <Link
              href="/marketplace"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/marketplace" ||
                pathname.startsWith("/marketplace/")
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              Marketplace
            </Link>

            <Link
              href="/elections"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/elections" || pathname.startsWith("/elections/")
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              Elections
            </Link>

            <Link
              href="/about"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/about"
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              About
            </Link>

            <Link
              href="/support"
              className={`px-3 py-1 rounded-md transition-colors ${
                pathname === "/support"
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "hover:text-primary dark:hover:text-primary-foreground"
              }`}
              onClick={toggleMenu}
            >
              Support
            </Link>
            <div className="flex flex-col space-y-2 pt-2 border-t border-gray-200 dark:border-gray-800">
              <AuthConditional
                unauthenticated={
                  <>
                    <Link
                      href="/login"
                      className="px-4 py-2 text-center rounded-full border border-gray-300 dark:border-gray-700 hover:bg-secondary transition-colors"
                      onClick={toggleMenu}
                    >
                      Log in
                    </Link>
                    <Link
                      href="/signup"
                      className="px-4 py-2 text-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      onClick={toggleMenu}
                    >
                      Sign up
                    </Link>
                  </>
                }
                authenticated={
                  <LogoutButton
                    onClick={() => {
                      // handleLogout();
                      toggleMenu();
                    }}
                    className="px-4 py-2 text-center rounded-full border border-gray-300 dark:border-gray-700 hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  >
                    Log out
                  </LogoutButton>
                }
              />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
